<script>
  import { fly } from "svelte/transition";
  
  export let experiences = [];
  export let activeIndex = 0;
  export let setActive;
  
  // Track previous position for animations
  let previousActive = activeIndex;
  $: if (previousActive !== activeIndex) {
    previousActive = activeIndex;
  }
</script>

<div class="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 sticky top-20">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Timeline</h3>
  
  <div class="relative">
    <div class="space-y-8">
      {#each experiences as exp, i}
        <button 
          class="relative flex items-start gap-4 py-2 w-full text-left transition-transform duration-300 
            hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 
            focus-visible:ring-blue-500/50 dark:focus-visible:ring-blue-400/50 rounded-lg"
          on:click={() => setActive(i)}
          aria-label={exp.position}
          in:fly={{ y: 20, delay: i * 100, duration: 400 }}
        >
          <!-- Main dot container with 3D effect -->
          <div class="relative flex-shrink-0 h-5 mt-0.5 transform" style="width: 24px;">
            <!-- Centered dot wrapper -->
            <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <!-- Base dot with glass effect -->
              <div class="w-2.5 h-2.5 rounded-full transition-all duration-500 ease-out-expo
                backdrop-blur-sm border border-white/20 dark:border-white/10
                {activeIndex === i 
                  ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 scale-125' 
                  : 'bg-gradient-to-r from-gray-300/80 to-gray-200/80 dark:from-gray-600/80 dark:to-gray-700/80 scale-100'}
                hover:shadow-lg hover:shadow-blue-500/20 dark:hover:shadow-blue-400/20
                relative z-10"
              >
                <!-- Glass shine effect -->
                <div class="absolute inset-0 rounded-full bg-gradient-to-br from-white/50 via-transparent to-black/20 
                  dark:from-white/30 dark:to-black/30">
                </div>
              </div>
              
              <!-- Active dot effects -->
              {#if activeIndex === i}
                <!-- Neon glow -->
                <div class="absolute inset-[-6px] rounded-full opacity-75
                  bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 
                  blur-[5px] -z-10 animate-glow">
                </div>
                
                <!-- Orbital rings -->
                <div class="absolute inset-[-10px] rounded-full border border-blue-500/20
                  animate-spin-slow">
                </div>
                <div class="absolute inset-[-8px] rounded-full border border-purple-500/20
                  animate-spin-slow-reverse">
                </div>

                <!-- Ripple effect on change -->
                <div class="absolute inset-[-15px] rounded-full 
                  bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10
                  animate-ripple-fade">
                </div>
              {/if}

              <!-- Interactive hover effects -->
              <div class="absolute inset-[-6px] rounded-full 
                bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 
                opacity-0 hover:opacity-20 transition-all duration-500 
                scale-0 hover:scale-100 blur-sm">
              </div>
            </div>
          </div>
          
          <!-- Progress line with flow effect - vertical version -->
          {#if i < experiences.length - 1}
            <div class="absolute top-[calc(100%-8px)] left-3 h-8 -translate-x-1/2 pointer-events-none">
              <div class="relative w-0.5 h-full transition-all duration-700 ease-out-expo overflow-hidden rounded-full
                {i < activeIndex 
                  ? 'bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 scale-y-100' 
                  : 'bg-gray-200/50 dark:bg-gray-700/50 scale-y-75'}">
                
                <!-- Line effects -->
                {#if i < activeIndex}
                  <!-- Flowing light effect -->
                  <div class="absolute inset-0 h-[200%] bg-gradient-to-b from-transparent 
                    via-white/50 to-transparent -translate-y-full animate-flow">
                  </div>
                {/if}
              </div>
            </div>
          {/if}
          
          <div class="transition-all duration-300 {activeIndex === i ? 'transform translate-x-1' : ''}">
            <!-- Position title with gradient text for active item -->
            <p class="font-medium text-sm transition-all duration-300 
              {activeIndex === i 
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 font-semibold scale-105' 
                : 'text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400'}">
              {exp.position}
            </p>
            
            <!-- Date with highlight for active item -->
            <p class="text-xs mt-0.5 font-mono transition-all duration-300
              {activeIndex === i 
                ? 'text-gray-700 dark:text-gray-300' 
                : 'text-gray-500 dark:text-gray-400'}">
              {exp.displayStart} - {exp.displayEnd}
            </p>
          </div>
        </button>
      {/each}
    </div>
  </div>
</div>

<style>
  @keyframes glow {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.1); }
  }

  @keyframes flow {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
  }

  @keyframes ripple-fade {
    0% { transform: scale(0.8); opacity: 0.5; }
    100% { transform: scale(1.5); opacity: 0; }
  }
  
  @keyframes expand {
    0% { width: 0; }
    100% { width: 100%; }
  }

  .animate-glow {
    animation: glow 3s ease-in-out infinite;
  }

  .animate-flow {
    animation: flow 2s linear infinite;
  }

  .animate-spin-slow {
    animation: spin 8s linear infinite;
  }

  .animate-spin-slow-reverse {
    animation: spin 6s linear infinite reverse;
  }

  .animate-ripple-fade {
    animation: ripple-fade 1s ease-out forwards;
  }
  
  .animate-expand {
    animation: expand 0.5s ease-out forwards;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .ease-out-expo {
    transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
  }
</style> 